// components/extras/ExtraItemsTable.js
// Tabella degli item per una categoria specifica

import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Check, X, Package, Ruler, Hash } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { parseItalianNumber, normalizeNumericInput, formatItalianNumber } from '../../lib/numericUtils';
import MeasurementLinesEditor, { computeTotalFromMeasurementLines } from '../estimates/MeasurementLinesEditor';

const ExtraItemsTable = ({
  currentCategory,
  categoryItems,
  categoryTotal,
  formData,
  setFormData,
  activeCategory,
  onShowAddItemModal,
  onUpdateItem,
  onRemoveItem,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  extraType = 'lavoro'
}) => {
  return (
    <Card className="card-abitalo">
      {/* Category Header */}
      <div className="bg-[#FF7819] text-white px-4 py-3 flex items-center justify-between rounded-t-lg">
        <h3 className="font-semibold">{currentCategory?.code} - {currentCategory?.label}</h3>
        <Button
          size="sm"
          variant="secondary"
          className="bg-white text-[#FF7819] hover:bg-white/90"
          onClick={onShowAddItemModal}
          data-testid="add-item-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi Voce
        </Button>
      </div>

      <CardContent className="p-4">
        {/* Items Table */}
        {categoryItems.length === 0 ? (
          <EmptyState onShowAddItemModal={onShowAddItemModal} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="extra-items-table">
              <thead>
                <tr className="border-b border-[#DCDCDC]">
                  <th className="text-left py-2 px-2 text-[#737373] font-medium">N°</th>
                  <th className="text-left py-2 px-2 text-[#737373] font-medium">Codice - Descrizione</th>
                  <th className="text-center py-2 px-2 text-[#737373] font-medium w-20">U.M.</th>
                  <th className="text-center py-2 px-2 text-[#737373] font-medium w-24">Q.tà</th>
                  <th className="text-right py-2 px-2 text-[#737373] font-medium w-28">Prezzo Unit.</th>
                  <th className="text-right py-2 px-2 text-[#737373] font-medium w-28">Totale</th>
                  <th className="text-center py-2 px-2 text-[#737373] font-medium w-16">IVA%</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {categoryItems.map((item, index) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    onUpdateItem={onUpdateItem}
                    onRemoveItem={onRemoveItem}
                    editingNoteId={editingNoteId}
                    setEditingNoteId={setEditingNoteId}
                    editingNoteValue={editingNoteValue}
                    setEditingNoteValue={setEditingNoteValue}
                    extraType={extraType}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#FAFAFA]">
                  <td colSpan={5} className="py-3 px-2 text-right font-medium">
                    Totale {currentCategory?.label}:
                  </td>
                  <td className="py-3 px-2 text-right font-bold text-[#FF7819]">
                    {formatCurrency(categoryTotal)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Category Notes */}
        <div className="mt-4 pt-4 border-t border-[#F2F2F2]">
          <Label htmlFor={`note-${activeCategory}`}>Note categoria {currentCategory?.label}</Label>
          <Textarea
            id={`note-${activeCategory}`}
            value={formData.category_notes[activeCategory] || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              category_notes: {
                ...prev.category_notes,
                [activeCategory]: e.target.value
              }
            }))}
            placeholder="Note specifiche per questa categoria..."
            rows={2}
            className="mt-1"
            data-testid="category-notes-textarea"
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState = ({ onShowAddItemModal }) => (
  <div className="text-center py-8 text-[#737373]" data-testid="empty-category-state">
    <Package className="w-12 h-12 mx-auto mb-3 text-[#DCDCDC]" />
    <p>Nessuna voce in questa categoria</p>
    <Button
      variant="outline"
      className="mt-4"
      onClick={onShowAddItemModal}
    >
      <Plus className="w-4 h-4 mr-1" />
      Aggiungi la prima voce
    </Button>
  </div>
);

// Individual item row component
const ItemRow = ({ 
  item, 
  index, 
  onUpdateItem, 
  onRemoveItem,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  extraType = 'lavoro'
}) => {
  // Stato locale per permettere digitazione libera
  const [localQuantity, setLocalQuantity] = useState(String(item.quantity));

  // STEP 4F.1 · Misure — solo per Extra lavoro (le Scelte/forniture 4E restano invariate)
  const isLavoro = extraType === 'lavoro';
  const quantityMode = item.quantity_mode || 'single';
  const isDetailed = isLavoro && quantityMode === 'detailed';
  const measurementLines = Array.isArray(item.measurement_lines) ? item.measurement_lines : [];

  const handleModeChange = (newMode) => {
    if (newMode === quantityMode) return;
    onUpdateItem(item.id, 'quantity_mode', newMode);
    if (newMode === 'detailed' && measurementLines.length === 0) {
      onUpdateItem(item.id, 'measurement_lines', []);
    }
    // Passando a single si conservano le righe (come nel Preventivo classico) e si usa quantity corrente
  };

  const handleMeasurementLinesChange = (newLines) => {
    const totalQty = computeTotalFromMeasurementLines(newLines);
    onUpdateItem(item.id, 'measurement_lines', newLines);
    onUpdateItem(item.id, 'quantity', totalQty);
  };
  
  // Sync quando item.quantity cambia dall'esterno
  React.useEffect(() => {
    setLocalQuantity(String(item.quantity));
  }, [item.quantity]);
  
  const handleQuantityChange = (e) => {
    const normalized = normalizeNumericInput(e.target.value);
    setLocalQuantity(normalized);
  };
  
  const handleQuantityBlur = () => {
    const parsed = parseItalianNumber(localQuantity, 0);
    const finalValue = Math.max(0, parsed);
    setLocalQuantity(String(finalValue));
    onUpdateItem(item.id, 'quantity', finalValue);
  };
  
  return (
    <React.Fragment>
      <tr className="border-b border-[#F2F2F2] hover:bg-[#FAFAFA]" data-testid={`item-row-${item.id}`}>
        <td className="py-2 px-2 text-[#737373]">{index + 1}</td>
        <td className="py-2 px-2">
          <div>
            <span className="font-mono text-xs text-[#FF7819]">{item.code}</span>
            <p className="text-[#3C3C3C]">{item.description}</p>
            {item.subcategory && (
              <span className="text-xs text-[#737373]">{item.subcategory}</span>
            )}
            {/* STEP 4F.1 · Toggle Misura totale / Misure parziali (solo Extra lavoro) */}
            {isLavoro && (
              <div className="inline-flex mt-1 rounded-md border border-[#e5e7eb] overflow-hidden" data-testid={`qty-mode-toggle-${item.id}`}>
                <button
                  type="button"
                  onClick={() => handleModeChange('single')}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[11px] transition-colors ${quantityMode === 'single' ? 'bg-[#FF7819] text-white' : 'bg-white text-[#737373] hover:bg-[#fafafa]'}`}
                  data-testid={`extra-mode-single-${item.id}`}
                >
                  <Hash className="w-3 h-3" /> Misura totale
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('detailed')}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[11px] transition-colors ${quantityMode === 'detailed' ? 'bg-[#FF7819] text-white' : 'bg-white text-[#737373] hover:bg-[#fafafa]'}`}
                  data-testid={`extra-mode-detailed-${item.id}`}
                >
                  <Ruler className="w-3 h-3" /> Misure parziali
                </button>
              </div>
            )}
          </div>
        </td>
        <td className="py-2 px-2 text-center">{item.unit}</td>
        <td className="py-2 px-2">
          {isDetailed ? (
            <div className="w-20 text-center font-semibold text-[#FF7819] text-sm" data-testid={`item-quantity-detailed-${item.id}`}>
              {(item.quantity || 0).toString().replace('.', ',')}
            </div>
          ) : (
            <Input
              type="text"
              inputMode="decimal"
              value={localQuantity}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              className="w-20 text-center h-8"
              data-testid={`item-quantity-${item.id}`}
            />
          )}
        </td>
        <td className="py-2 px-2 text-right">
          {isLavoro && item.list_unit_price != null && Number(item.list_unit_price) !== Number(item.unit_price) ? (
            <div className="flex flex-col items-end leading-tight" data-testid={`item-price-policy-${item.id}`}>
              <span className="text-[10px] text-[#a3a3a3] line-through">{formatCurrency(item.list_unit_price)}</span>
              <span className="font-medium text-[#3C3C3C]">{formatCurrency(item.unit_price)}</span>
            </div>
          ) : (
            formatCurrency(item.unit_price)
          )}
        </td>
        <td className="py-2 px-2 text-right font-medium">
          {formatCurrency(item.total)}
        </td>
        <td className="py-2 px-2 text-center text-[#737373]">
          {item.vat_percentage}%
        </td>
        <td className="py-2 px-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemoveItem(item.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`remove-item-${item.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </td>
      </tr>
      {/* STEP 4F.1 · Editor righe misura (modalità dettagliata) */}
      {isDetailed && (
        <tr data-testid={`measurement-row-${item.id}`}>
          <td></td>
          <td colSpan={7} className="py-1 px-2 pb-2 border-b border-[#F2F2F2]">
            <MeasurementLinesEditor
              lines={measurementLines}
              unit={item.unit}
              onChange={handleMeasurementLinesChange}
            />
          </td>
        </tr>
      )}
      {/* Note toggle row */}
      <tr>
        <td></td>
        <td colSpan={7} className="py-1 px-2 pb-2 border-b border-[#F2F2F2]">
          <NoteEditor
            item={item}
            editingNoteId={editingNoteId}
            setEditingNoteId={setEditingNoteId}
            editingNoteValue={editingNoteValue}
            setEditingNoteValue={setEditingNoteValue}
            onUpdateItem={onUpdateItem}
          />
        </td>
      </tr>
    </React.Fragment>
  );
};

// Note editor sub-component
const NoteEditor = ({
  item,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  onUpdateItem
}) => {
  if (editingNoteId === item.id) {
    return (
      <div className="flex items-center gap-2 mt-1">
        <Input
          value={editingNoteValue}
          onChange={(e) => setEditingNoteValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onUpdateItem(item.id, 'notes', editingNoteValue);
              setEditingNoteId(null);
              setEditingNoteValue('');
            } else if (e.key === 'Escape') {
              setEditingNoteId(null);
              setEditingNoteValue('');
            }
          }}
          placeholder="Inserisci nota..."
          className="text-sm h-8 flex-1"
          autoFocus
          data-testid={`note-input-${item.id}`}
        />
        <Button
          size="sm"
          className="h-8 px-2 bg-green-600 hover:bg-green-700"
          onClick={() => {
            onUpdateItem(item.id, 'notes', editingNoteValue);
            setEditingNoteId(null);
            setEditingNoteValue('');
          }}
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2"
          onClick={() => {
            setEditingNoteId(null);
            setEditingNoteValue('');
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  if (item.notes) {
    return (
      <div 
        className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 cursor-pointer hover:bg-amber-100 transition-colors mt-1"
        onClick={() => {
          setEditingNoteId(item.id);
          setEditingNoteValue(item.notes);
        }}
        data-testid={`note-display-${item.id}`}
      >
        <span className="font-bold text-amber-800">Nota:</span>{' '}
        <span className="italic text-amber-700">{item.notes}</span>
      </div>
    );
  }

  return (
    <button
      className="text-xs text-[#737373] hover:text-[#FF7819] flex items-center gap-1 mt-1"
      onClick={() => {
        setEditingNoteId(item.id);
        setEditingNoteValue('');
      }}
      data-testid={`add-note-btn-${item.id}`}
    >
      <Plus className="w-3 h-3" />
      Aggiungi nota
    </button>
  );
};

export default ExtraItemsTable;
